import React from 'react';
import { colors, spacing, borderRadius, transitions, shadows } from '../../styles/designTokens';
import LoadingSpinner from './LoadingSpinner';

/**
 * Button Component
 * 
 * Standardized button component with multiple variants and states.
 * ALWAYS use this component instead of creating one-off styled buttons.
 * 
 * Usage:
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Save Changes
 * </Button>
 * 
 * <Button variant="danger" loading={isSaving} disabled={!isValid}>
 *   Delete User
 * </Button>
 * 
 * Props:
 * - variant: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' (default: 'primary')
 * - size: 'sm' | 'md' | 'lg' (default: 'md')
 * - loading: Boolean - shows loading spinner
 * - disabled: Boolean - disables button
 * - fullWidth: Boolean - makes button full width
 * - icon: Optional icon element (placed before children)
 * - iconRight: Optional icon element (placed after children)
 * - className: Additional CSS classes
 * - All other props are passed to the button element
 */

const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  icon,
  iconRight,
  children,
  className = '',
  type = 'button',
  ...props
}) => {
  // Size configurations
  const sizeConfig = {
    sm: {
      padding: '0.5rem 0.75rem',   // py-2 px-3
      fontSize: '0.875rem',         // text-sm
      height: '2rem'               // h-8
    },
    md: {
      padding: '0.625rem 1rem',    // py-2.5 px-4
      fontSize: '0.875rem',         // text-sm
      height: '2.5rem'             // h-10
    },
    lg: {
      padding: '0.75rem 1.5rem',   // py-3 px-6
      fontSize: '1rem',             // text-base
      height: '3rem'               // h-12
    }
  };

  // Variant configurations
  const variantConfig = {
    primary: {
      bg: colors.primary.main,
      bgHover: colors.primary.dark,
      text: '#FFFFFF',
      border: 'transparent',
      shadow: shadows.sm
    },
    secondary: {
      bg: colors.neutral[100],
      bgHover: colors.neutral[200],
      text: colors.neutral[700],
      border: colors.neutral[300],
      shadow: shadows.sm
    },
    outline: {
      bg: 'transparent',
      bgHover: colors.neutral[50],
      text: colors.primary.main,
      border: colors.primary.main,
      shadow: 'none'
    },
    ghost: {
      bg: 'transparent',
      bgHover: colors.neutral[100],
      text: colors.neutral[700],
      border: 'transparent',
      shadow: 'none'
    },
    danger: {
      bg: colors.error.main,
      bgHover: colors.error.dark,
      text: '#FFFFFF',
      border: 'transparent',
      shadow: shadows.sm
    }
  };

  const sizeStyles = sizeConfig[size] || sizeConfig.md;
  const variantStyles = variantConfig[variant] || variantConfig.primary;

  // Disabled or loading state
  const isDisabled = disabled || loading;

  // Base button styles
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    borderRadius: borderRadius.md,
    transition: transitions.normal,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    border: `1px solid ${variantStyles.border}`,
    outline: 'none',
    width: fullWidth ? '100%' : 'auto',
    ...sizeStyles,
    backgroundColor: variantStyles.bg,
    color: variantStyles.text,
    boxShadow: variantStyles.shadow
  };

  // Hover styles (applied via onMouseEnter/onMouseLeave)
  const handleMouseEnter = (e) => {
    if (!isDisabled) {
      e.currentTarget.style.backgroundColor = variantStyles.bgHover;
    }
  };

  const handleMouseLeave = (e) => {
    if (!isDisabled) {
      e.currentTarget.style.backgroundColor = variantStyles.bg;
    }
  };

  // Focus styles
  const handleFocus = (e) => {
    e.currentTarget.style.outline = `2px solid ${colors.primary.main}`;
    e.currentTarget.style.outlineOffset = '2px';
  };

  const handleBlur = (e) => {
    e.currentTarget.style.outline = 'none';
  };

  return (
    <button
      type={type}
      disabled={isDisabled}
      className={className}
      style={baseStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-disabled={isDisabled}
      aria-busy={loading}
      {...props}
    >
      {/* Loading spinner */}
      {loading && (
        <span className="mr-2">
          <LoadingSpinner 
            size="small" 
            inline 
            color={variantStyles.text}
          />
        </span>
      )}

      {/* Left icon */}
      {!loading && icon && (
        <span 
          className="mr-2 flex items-center" 
          aria-hidden="true"
        >
          {icon}
        </span>
      )}

      {/* Button content */}
      <span>
        {children}
      </span>

      {/* Right icon */}
      {!loading && iconRight && (
        <span 
          className="ml-2 flex items-center" 
          aria-hidden="true"
        >
          {iconRight}
        </span>
      )}
    </button>
  );
};

/**
 * IconButton Component
 * 
 * Button with only an icon (no text)
 * MUST include aria-label for accessibility
 * 
 * Usage:
 * <IconButton 
 *   icon={<TrashIcon />} 
 *   aria-label="Delete user"
 *   onClick={handleDelete}
 * />
 */
export const IconButton = ({
  icon,
  size = 'md',
  variant = 'ghost',
  'aria-label': ariaLabel,
  ...props
}) => {
  if (!ariaLabel) {
    console.error('IconButton requires aria-label for accessibility');
  }

  const sizeMap = {
    sm: '2rem',
    md: '2.5rem',
    lg: '3rem'
  };

  return (
    <Button
      variant={variant}
      size={size}
      aria-label={ariaLabel}
      style={{
        width: sizeMap[size],
        height: sizeMap[size],
        padding: 0
      }}
      {...props}
    >
      {icon}
    </Button>
  );
};

/**
 * ButtonGroup Component
 * 
 * Groups multiple buttons together
 * 
 * Usage:
 * <ButtonGroup>
 *   <Button>Option 1</Button>
 *   <Button>Option 2</Button>
 *   <Button>Option 3</Button>
 * </ButtonGroup>
 */
export const ButtonGroup = ({ children, className = '' }) => {
  return (
    <div 
      className={`inline-flex ${className}`}
      role="group"
    >
      {React.Children.map(children, (child, index) => {
        if (!child) return null;
        
        // Add styles for grouped buttons
        const isFirst = index === 0;
        const isLast = index === React.Children.count(children) - 1;
        
        return React.cloneElement(child, {
          style: {
            ...child.props.style,
            borderRadius: isFirst 
              ? `${borderRadius.md} 0 0 ${borderRadius.md}`
              : isLast
                ? `0 ${borderRadius.md} ${borderRadius.md} 0`
                : '0',
            marginLeft: index > 0 ? '-1px' : '0'
          }
        });
      })}
    </div>
  );
};

/**
 * LinkButton Component
 * 
 * Button styled as a link
 * 
 * Usage:
 * <LinkButton onClick={handleNavigation}>
 *   Go to Dashboard
 * </LinkButton>
 */
export const LinkButton = ({ 
  children, 
  className = '',
  ...props 
}) => {
  return (
    <button
      type="button"
      className={`text-sm font-medium transition-colors ${className}`}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: colors.primary.main,
        cursor: 'pointer',
        textDecoration: 'underline'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = colors.primary.dark;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = colors.primary.main;
      }}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;

